"use client";

import { useRef, useState, useEffect } from "react";
import { submitCareerApplication } from "@/lib/api";

/* ─── Scroll reveal ───────────────────────────────────────── */
function useVisible(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return [ref, visible] as const;
}

function Reveal({ children, delay = 0, from = "bottom" }: {
  children: React.ReactNode;
  delay?: number;
  from?: "bottom" | "left" | "right";
}) {
  const [ref, v] = useVisible();
  const hidden = {
    bottom: "opacity-0 translate-y-7",
    left:   "opacity-0 -translate-x-7",
    right:  "opacity-0 translate-x-7",
  };
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${v ? "opacity-100 translate-x-0 translate-y-0" : hidden[from]}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ─── Shared UI ───────────────────────────────────────────── */
function Eyebrow({ text }: { text: string }) {
  return (
    <span className="block text-[11px] font-bold tracking-[0.15em] uppercase text-blue-700 mb-3">
      {text}
    </span>
  );
}
function Divider() {
  return <div className="w-10 h-[3px] bg-blue-700 mb-5" />;
}

/* ─── Data ────────────────────────────────────────────────── */
const PILLARS = [
  {
    title: "The Farsight Way",
    body: "We are a company built on the purpose of creating meaningful impact for our people and the businesses we serve. Here, you will find a culture that values initiative, rewards performance, and invests in your long-term career — the Farsight way.",
    cta: "Learn More",
    img: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80",
    imgSide: "right",
  },
  {
    title: "Diversity & Inclusion",
    body: "The different. The difference. We celebrate both. Farsight is intentionally diverse — across backgrounds, disciplines, and perspectives. We believe the best ideas emerge when varied minds work toward a shared mission.",
    cta: "Our Culture",
    img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&q=80",
    imgSide: "left",
  },
  {
    title: "Reconnect with Us",
    body: "Once a Farsighter, always a Farsighter. Explore return opportunities, stay connected through our alumni community, and be part of our growing professional network. Your story with us does not have to end.",
    cta: "Alumni Portal",
    img: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&q=80",
    imgSide: "right",
  },
];

const VALUES = [
  { title: "Growth Mindset",       body: "Continuous learning, upskilling, and career development opportunities at every level." },
  { title: "Ownership Culture",    body: "We give people real responsibility from day one — your work has visible, measurable impact." },
  { title: "Collaborative Teams",  body: "Small, cross-functional teams where everyone's voice matters and ideas get implemented." },
  { title: "Work–Life Balance",    body: "Flexible working, generous leave, and a culture that respects your time outside of work." },
  { title: "Competitive Rewards",  body: "Performance-linked compensation, transparent growth paths, and meaningful recognition." },
  { title: "Purpose-Driven Work",  body: "Every role contributes directly to helping Indian businesses grow, scale, and succeed." },
];

/* ─── Application Form ────────────────────────────────────── */
function ApplicationForm() {
  const [form, setForm] = useState({
    fullName: "", email: "", phone: "", city: "",
    department: "", linkedin: "", portfolio: "", message: "",
  });
  const [resume, setResume] = useState<File | null>(null);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const fileRef = useRef<HTMLInputElement>(null);

  const departments = [
    "Business Consultancy", "Legal & Compliance", "Finance & Funding",
    "Sales & Business Development", "Technology / IT", "Marketing & Growth",
    "Operations", "Human Resources", "Internship",
  ];

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("sending");

    try {
      const fd = new FormData();
      fd.append('fullName', form.fullName);
      fd.append('email', form.email);
      fd.append('phone', form.phone);
      fd.append('city', form.city);
      fd.append('department', form.department);
      fd.append('linkedin', form.linkedin);
      fd.append('portfolio', form.portfolio);
      fd.append('message', form.message);
      if (resume) fd.append('resume', resume);
      await submitCareerApplication(fd);
      setStatus("sent");
      setForm({ fullName: "", email: "", phone: "", city: "", department: "", linkedin: "", portfolio: "", message: "" });
      setResume(null);
    } catch {
      setStatus("error");
    }
  }

  const inputCls = "w-full bg-white border border-slate-200 text-[14px] text-slate-800 placeholder:text-slate-400 px-4 py-3 focus:outline-none focus:border-blue-700 transition-colors";
  const labelCls = "block text-[11px] font-bold tracking-[0.1em] uppercase text-slate-500 mb-1.5";

  return (
    <section className="py-10 sm:py-14 lg:py-20 px-4 sm:px-8 lg:px-20 bg-slate-50" id="apply">
      <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

        {/* Left: copy */}
        <Reveal from="left">
          <Eyebrow text="Join Our Team" />
          <Divider />
          <h2 className="text-[clamp(24px,3vw,40px)] font-bold text-slate-900 leading-tight mb-5">
            Apply and Start Your Farsight Journey
          </h2>
          <p className="text-[15px] text-slate-500 leading-relaxed mb-8">
            We are always looking for talented, driven individuals who want to make a real difference. Whether you are early in your career or a seasoned professional, there is a place for you at Farsight Solutions.
          </p>

          {/* Contact info */}
          <div className="flex flex-col gap-4 mb-8">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-blue-700 flex-shrink-0 flex items-center justify-center mt-0.5">
                <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                  <path d="M1 2.5h8M1 5h6M1 7.5h4" stroke="white" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
              </div>
              <div>
                <p className="text-[12px] font-bold text-slate-400 tracking-[0.08em] uppercase mb-0.5">Email us directly</p>
                <a href="mailto:hr@farsight.co.in" className="text-[14px] font-semibold text-blue-700 hover:underline">hr@farsight.co.in</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-blue-700 flex-shrink-0 flex items-center justify-center mt-0.5">
                <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                  <circle cx="5" cy="5" r="3.5" stroke="white" strokeWidth="1.4"/>
                  <path d="M5 3v2l1.2 1.2" stroke="white" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
              </div>
              <div>
                <p className="text-[12px] font-bold text-slate-400 tracking-[0.08em] uppercase mb-0.5">Response time</p>
                <p className="text-[14px] text-slate-600">We respond to all applications within 5 business days.</p>
              </div>
            </div>
          </div>

          {/* Values quick list */}
          <div className="border-t border-slate-200 pt-7">
            <p className="text-[11px] font-bold tracking-[0.1em] uppercase text-slate-400 mb-4">Why join Farsight?</p>
            <div className="flex flex-col gap-2.5">
              {["Performance-linked compensation & transparent growth paths", "Ownership from day one — real responsibility, visible impact", "Flexible working, generous leave, and work-life balance", "Direct contribution to helping Indian businesses grow"].map((pt, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-4 h-4 bg-blue-700 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                      <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-[13px] text-slate-600 leading-snug">{pt}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Right: form */}
        <Reveal from="right" delay={0.1}>
          <div className="bg-white border border-slate-200 p-8">
            <p className="text-[15px] font-bold text-slate-900 mb-1">Submit Your Application</p>
            <p className="text-[13px] text-slate-400 mb-7">All fields marked <span className="text-blue-700">*</span> are required.</p>

            {status === "sent" ? (
              <div className="text-center py-16">
                <div className="w-12 h-12 bg-blue-700 flex items-center justify-center mx-auto mb-4">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M3 10.5L7.5 15L17 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="text-[17px] font-bold text-slate-900 mb-2">Application Submitted</p>
                <p className="text-[14px] text-slate-500 leading-relaxed max-w-[280px] mx-auto">
                  Thank you for your interest in Farsight Solutions. Our HR team will review your application and be in touch within 5 business days.
                </p>
                <button onClick={() => setStatus("idle")} className="mt-6 text-[12px] font-semibold text-blue-700 underline cursor-pointer bg-transparent border-none">
                  Submit another application
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                {/* Row 1: Full name */}
                <div>
                  <label className={labelCls}>Full Name <span className="text-blue-700">*</span></label>
                  <input required name="fullName" value={form.fullName} onChange={handleChange} placeholder="Your full name" className={inputCls} />
                </div>

                {/* Row 2: Email + Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Email Address <span className="text-blue-700">*</span></label>
                    <input required type="email" name="email" value={form.email} onChange={handleChange} placeholder="Please enter your email" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Phone Number <span className="text-blue-700">*</span></label>
                    <input required type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="Please enter your phone number" className={inputCls} />
                  </div>
                </div>

                {/* Row 3: City + Department */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Current City <span className="text-blue-700">*</span></label>
                    <input required name="city" value={form.city} onChange={handleChange} placeholder="Enter your current city" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Area of Interest <span className="text-blue-700">*</span></label>
                    <select required name="department" value={form.department} onChange={handleChange} className={inputCls + " cursor-pointer"}>
                      <option value="" disabled>Select department</option>
                      {departments.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>

                {/* Row 4: LinkedIn + Portfolio */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>LinkedIn Profile</label>
                    <input name="linkedin" value={form.linkedin} onChange={handleChange} placeholder="linkedin.com/in/yourname" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Portfolio / Website</label>
                    <input name="portfolio" value={form.portfolio} onChange={handleChange} placeholder="yourwebsite.com" className={inputCls} />
                  </div>
                </div>

                {/* Row 5: Resume upload */}
                <div>
                  <label className={labelCls}>Resume / CV <span className="text-blue-700">*</span></label>
                  <div
                    onClick={() => fileRef.current?.click()}
                    className="border border-dashed border-slate-300 hover:border-blue-700 transition-colors cursor-pointer px-4 py-5 flex flex-col items-center gap-2"
                  >
                    <div className="w-9 h-9 bg-slate-100 flex items-center justify-center">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 2v9M4 7l4-5 4 5M2 13h12" stroke="#1565C0" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    {resume ? (
                      <p className="text-[13px] font-semibold text-blue-700">{resume.name}</p>
                    ) : (
                      <>
                        <p className="text-[13px] font-semibold text-slate-600">Click to upload your resume</p>
                        <p className="text-[11px] text-slate-400">PDF, DOC, DOCX — max 5 MB</p>
                      </>
                    )}
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      onChange={e => setResume(e.target.files?.[0] ?? null)}
                      required
                    />
                  </div>
                </div>

                {/* Row 6: Cover message */}
                <div>
                  <label className={labelCls}>Why Farsight? <span className="text-blue-700">*</span></label>
                  <textarea
                    required
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Tell us briefly why you want to join Farsight Solutions and what you bring to the team..."
                    className={inputCls + " resize-none"}
                  />
                </div>

                {/* Error */}
                {status === "error" && (
                  <p className="text-[13px] text-red-600 font-medium">Something went wrong. Please email us directly at hr@farsight.co.in</p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="w-full bg-blue-700 text-white text-[13px] font-bold tracking-[0.08em] uppercase py-4 hover:bg-blue-800 transition-colors border-0 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "sending" ? "Submitting Application..." : "Submit Application"}
                </button>

                <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                  By submitting, you agree to our privacy policy. Your data will only be used for recruitment purposes and sent to hr@farsight.co.in.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── ValueCard ───────────────────────────────────────────── */
function ValueCard({ v, i }: { v: { title: string; body: string }; i: number }) {
  const [ref, vis] = useVisible();
  return (
    <div
      ref={ref}
      className={`bg-slate-50 border border-slate-200 p-7 transition-all duration-500 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
      style={{ transitionDelay: `${(i % 3) * 0.09}s` }}
    >
      <div className="w-8 h-8 bg-blue-700 flex items-center justify-center mb-4">
        <span className="text-[11px] font-black text-white">{String(i + 1).padStart(2, "0")}</span>
      </div>
      <p className="text-[14px] font-bold text-slate-900 mb-2.5">{v.title}</p>
      <p className="text-[13.5px] text-slate-500 leading-relaxed">{v.body}</p>
    </div>
  );
}

/* ─── Page ────────────────────────────────────────────────── */
export default function CareersPage() {
  return (
    <main className="bg-white">

      {/* ══ HERO ═══════════════════════════════════════════ */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[450px]">

        {/* Left: text on cream/light background — matching TechM style */}
        <div
          className="flex flex-col justify-center px-4 sm:px-8 lg:px-20 py-10 lg:py-20"
          style={{ background: "#F5F2ED" }}
        >
          <Eyebrow text="Careers at Farsight" />
          <h1 className="text-[clamp(32px,4vw,56px)] font-extrabold text-slate-900 leading-[1.08] mb-5 ]">
            Careers
          </h1>
          <p className="text-[16px] font-light text-slate-600 leading-relaxed ">
            Explore hyper-personalised, technology-led, human-centred experiences that create moments that matter — and careers that mean something.
          </p>

          <div className="flex gap-3.5 mt-9">
            <a
              href="#apply"
              className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors"
            >
              APPLY NOW
            </a>
            <a
              href="#life"
              className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-7 py-3.5 border border-slate-300 hover:border-slate-500 transition-colors"
            >
              LIFE AT FARSIGHT
            </a>
          </div>
        </div>

        {/* Right: full-bleed photo */}
        <div className="relative overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=900&q=85"
            alt="Farsight team at work"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Blue stat strip */}
      <div className="bg-blue-700 grid grid-cols-2 lg:grid-cols-4">
        {[
          { v: "50+",   l: "Team Members" },
          { v: "100+",  l: "Clients Served" },
          { v: "10+",   l: "Departments" },
          { v: "2024",  l: "Founded" },
        ].map((s, i) => (
          <div key={i} className={`py-5 text-center ${i < 3 ? "border-r border-white/15" : ""}`}>
            <p className="text-[22px] font-black text-white leading-none">{s.v}</p>
            <p className="text-[11px] font-medium text-white/65 mt-1.5 tracking-widest uppercase">{s.l}</p>
          </div>
        ))}
      </div>

      {/* ══ THREE PILLARS — alternating layout (Tech Mahindra style) ══ */}
      <div id="life">
        {PILLARS.map((pillar, idx) => {
          const imgLeft = pillar.imgSide === "left";
          return (
            <section
              key={idx}
              className="py-10 sm:py-14 lg:py-20 px-4 sm:px-8 lg:px-20"
              style={{ background: idx === 1 ? "#F5F2ED" : "#FFFFFF" }}
            >
              <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">

                {/* Image side */}
                {imgLeft && (
                  <Reveal from="left">
                    <img src={pillar.img} alt={pillar.title} className="w-full h-[200px] sm:h-[260px] lg:h-[320px] object-cover"/>
                  </Reveal>
                )}

                {/* Text side */}
                <Reveal from={imgLeft ? "right" : "left"} delay={0.12}>
                  <div>
                    <h2 className="text-[clamp(22px,2.5vw,34px)] font-bold text-slate-900 leading-tight mb-4">
                      {pillar.title}
                    </h2>
                    <p className="text-[15px] text-slate-500 leading-relaxed mb-6 max-w-[400px]">
                      {pillar.body}
                    </p>
                    <a
                      href={idx === 2 ? "#apply" : "#"}
                      className="inline-block bg-blue-700 text-white text-[12px] font-bold tracking-[0.08em] uppercase px-6 py-3 hover:bg-blue-800 transition-colors"
                    >
                      {pillar.cta}
                    </a>
                  </div>
                </Reveal>

                {/* Image right side */}
                {!imgLeft && (
                  <Reveal from="right" delay={0.12}>
                    <img src={pillar.img} alt={pillar.title} className="w-full h-[200px] sm:h-[260px] lg:h-[320px] object-cover"/>
                  </Reveal>
                )}
              </div>
            </section>
          );
        })}
      </div>

      {/* ══ WHY WORK WITH US — 2×3 values grid ════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-20 items-end mb-10 lg:mb-14">
              <div>
                <Eyebrow text="Life at Farsight" />
                <Divider />
                <h2 className="text-[clamp(24px,3vw,40px)] font-bold text-slate-900 leading-tight m-0">
                  Why Work With Us
                </h2>
              </div>
              <p className="text-[15px] text-slate-500 leading-relaxed self-end pb-1">
                A culture built around growth, ownership, and purpose — where your career is as important as the clients we serve.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0.5">
            {VALUES.map((v, i) => (
              <ValueCard key={i} v={v} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ══ OPEN ROLES TEASER ══════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20" style={{ background: "#F5F2ED" }}>
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <img
              src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80"
              alt="Farsight careers"
              className="w-full h-[260px] sm:h-[340px] lg:h-[420px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <Eyebrow text="Current Openings" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,40px)] font-bold text-slate-900 leading-tight mb-5">
              We Are Actively Hiring Across Departments
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-8">
              From business consultants and legal specialists to software engineers, marketing strategists, and client success managers — we have roles across all disciplines. If you bring the right attitude and skillset, we want to hear from you.
            </p>

            <div className="flex flex-col gap-0.5 mb-8">
              {[
                "Business Consultancy & MSME Advisory",
                "Legal, Compliance & Corporate Governance",
                "Technology — Software, Mobile & Cloud",
                "Digital Marketing & Growth",
                "Finance, Funding & Investor Relations",
                "Sales & Business Development",
              ].map((role, i) => (
                <div key={i} className="flex items-center justify-between bg-white border border-slate-200 px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-blue-700 flex-shrink-0" />
                    <span className="text-[13.5px] font-semibold text-slate-800">{role}</span>
                  </div>
                  <span className="text-[11px] font-bold text-blue-700 tracking-wide uppercase">Multiple Roles</span>
                </div>
              ))}
            </div>

            <a
              href="#apply"
              className="inline-block bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors"
            >
              APPLY NOW
            </a>
          </Reveal>
        </div>
      </section>

      {/* ══ APPLICATION FORM ════════════════════════════════ */}
      <ApplicationForm />

      {/* ══ CONNECT & GROW — full-width (matching TechM) ══ */}
      <section className="relative min-h-[280px] flex items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1453928582365-b6ad33cbcf64?w=1400&q=80"
          alt="Connect and grow"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="relative text-center px-8 py-16 max-w-[680px] mx-auto">
          <Reveal>
            <h2 className="text-[clamp(26px,3.5vw,44px)] font-extrabold text-white leading-tight mb-4">
              Connect and Grow
            </h2>
            <p className="text-[16px] font-light text-white/75 leading-relaxed mb-8">
              At Farsight Solutions, you are not just joining a company — you are joining a community built to help Indian businesses and the people behind them grow without limits.
            </p>
            <a
              href="#apply"
              className="inline-block bg-white text-blue-700 text-[13px] font-bold tracking-[0.08em] uppercase px-8 py-3.5 hover:bg-slate-100 transition-colors"
            >
              JOIN US
            </a>
          </Reveal>
        </div>
      </section>

    </main>
  );
}